import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { ProductVariant, AuditEvent, SaleEventData } from "../types/api";

import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { getProductWithVariantGuid, getSaleEvents } from "src/services/product.service";
import { getPurchaseOrders } from "src/services/purchasing.service";
import _ from "lodash";

const parseSaleEventData = (dataString: string): SaleEventData => {
  if (!dataString) {
    return { quantity: 0 };
  }

  try {
    return JSON.parse(dataString);
  } catch (jsonError) {
    const quantityMatch = dataString.match(/quantity:\s*(\d+)/);
    const reasonMatch = dataString.match(/reason:\s*["']([^"']+)["']/);
    
    return {
      quantity: quantityMatch ? parseInt(quantityMatch[1], 10) : 0,
      reason: reasonMatch ? reasonMatch[1] : undefined,
    };
  }
};

export const ViewVariantStockModal = (props: any) => {
  const [open, setOpen] = useState(false);
  const [saleEvents, setSaleEvents] = useState<AuditEvent[]>([]);
  const [currentVariant, setCurrentVariant] = useState<
    ProductVariant | { error: boolean }
  >();

  useEffect(() => {
    getProductWithVariantGuid(props.variant.guid).then((p) => {
      // Avoid doing the work if we're not open
      if (!open) return;

      // There may be no product with this variant, if this was a SPE or historical product like a pack
      if (!p) {
        return setCurrentVariant({ error: true });
      }

      const v = p.variants.filter((v: any) => v.sku === props.variant.sku)[0];

      getPurchaseOrders(
        {
          filterModel: [{ field: "state", operator: "equals", value: "active" }],
          searchTerm: v.sku,
        },
        (data) => {
          // All these POs have the sku in the line items, but some will have been received.
          // Add up all the "ordered", and all the "received" and the incoming is the difference

          const skuData = data
            .map((po: any) => po.lineItems)
            .reduce((a: any[], b: any[]) => [...a, ...b], []) // flatten all line items to one array
            .map((poli: any) => [
              poli.variantSku,
              poli.quantityOrdered,
              poli.receipts,
            ])
            .filter((triple: any[]) => triple[0] === v.sku); // filter for the SKU we want

          const quantityOrdered = !_.isEmpty(skuData)
            ? skuData.map((sd: any[]) => sd[1]).reduce((a: number, b: number) => a + b)
            : 0;

          const received =
            quantityOrdered > 0
              ? skuData
                  .map((sd: any[]) => sd[2])
                  .filter((a: any[]) => a.length > 0)
                  .reduce((a: any[], b: any[]) => [...a, ...b], [])
                  .map((i: any) => (i.received ? i.received : 0))
                  .reduce((a: number, b: number) => a + b, 0)
              : 0;

          v.incoming = quantityOrdered - received;

          getSaleEvents(
            v.sku,
            (sales: any) => setSaleEvents(sales),
            () => {}
          );

          setCurrentVariant(v);
        },
        () => {}
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, props.variantSku]);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        disabled={props.disabled}
        size={props.size}
        sx={props.sx}
      >
        <VisibilityIcon />
      </IconButton>
      <Dialog onClose={props.onCancel} open={open}>
        <DialogTitle>
          Activity for: {props.variant.sku}
          <IconButton
            onClick={handleClose}
            sx={{
              marginLeft: 2,
              marginRight: -2,
              marginTop: -2,
              cursor: "pointer",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!currentVariant && <CircularProgress />}
          {currentVariant && 'error' in currentVariant && currentVariant.error && (
            <DialogContentText>
              Unable to show details for this variant, as it does not exist in
              the inventory. Maybe it is a historical product, such as a pack,
              or a special order?
            </DialogContentText>
          )}
          {currentVariant && !('error' in currentVariant) && (
            <DialogContentText>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell variant="head">
                        <strong>Available</strong>
                      </TableCell>
                      <TableCell>{currentVariant.stockOnHand}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head">
                        <strong>Incoming</strong>
                      </TableCell>
                      <TableCell>{(currentVariant as any).incoming}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head" colSpan={2}>
                        <strong>Sales History:</strong>
                        {_.isEmpty(saleEvents) && (
                          <Typography sx={{ p: "1em" }}>None</Typography>
                        )}
                        {!_.isEmpty(saleEvents) && (
                          <ul style={{ listStyle: "none", paddingLeft: "1em" }}>
                            {saleEvents.map((e, index) => {
                              const saleData = parseSaleEventData(e.data || "");
                              return (
                                <li key={index}>
                                  {saleData.quantity} sold on{" "}
                                  {new Date(e.createdAt).toLocaleDateString()} (
                                  {e.sourceId})
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContentText>
          )}
          <DialogActions>
            <div style={{ flex: "1 0 0" }} />
            <Button
              onClick={handleClose}
              color="secondary"
              variant="contained"
              size="small"
            >
              Close
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};
